import { useMemo } from 'react';

import { type RateLimitedActionType } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	CHAT_STORE_STATUS,
	type Conversation,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

type UseLatestMessageRenderStabilizationArgs = {
	conversation: Conversation | undefined;
	isRateLimitedState: boolean;
	isThinkingAgentHydrating: boolean;
	rateLimitedActionType: RateLimitedActionType | undefined;
	shouldUseStableOlderMessageRenderer: boolean;
	stableOlderMessageRenderVersion: NonNullable<unknown>;
};

type UseLatestMessageRenderStabilizationResult = {
	conversationStatus: CHAT_STORE_STATUS | undefined;
	conversationTracesCount: number;
	isConversationActivelyGenerating: boolean;
	shouldUseStableLatestMessageRenderer: boolean;
	stableLatestMessageRenderVersion: NonNullable<unknown>;
};

export const useLatestMessageRenderStabilization = ({
	conversation,
	isRateLimitedState,
	isThinkingAgentHydrating,
	rateLimitedActionType,
	shouldUseStableOlderMessageRenderer,
	stableOlderMessageRenderVersion,
}: UseLatestMessageRenderStabilizationArgs): UseLatestMessageRenderStabilizationResult => {
	const conversationStatus = conversation?.status;
	const conversationTracesCount = conversation?.traces?.length ?? 0;
	const isConversationActivelyGenerating =
		conversationStatus === CHAT_STORE_STATUS.awaitingResponse ||
		conversationStatus === CHAT_STORE_STATUS.typingResponse;

	const stableLatestMessageRenderVersion = useMemo(
		() => ({
			conversationStatus,
			conversationTracesCount,
			isRateLimitedState,
			isThinkingAgentHydrating,
			olderMessageRenderVersion: stableOlderMessageRenderVersion,
			rateLimitedActionType,
		}),
		[
			conversationStatus,
			conversationTracesCount,
			isRateLimitedState,
			isThinkingAgentHydrating,
			rateLimitedActionType,
			stableOlderMessageRenderVersion,
		],
	);

	const shouldUseStableLatestMessageRenderer =
		shouldUseStableOlderMessageRenderer &&
		!isConversationActivelyGenerating &&
		conversationTracesCount === 0 &&
		!isThinkingAgentHydrating &&
		!isRateLimitedState;

	return {
		conversationStatus,
		conversationTracesCount,
		isConversationActivelyGenerating,
		shouldUseStableLatestMessageRenderer,
		stableLatestMessageRenderVersion,
	};
};
